import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { Badge } from "../Badge";
import { Search } from "lucide-react";

export const REFERRAL_STATUSES = ["New", "Under Review", "Contacted", "Assessment Scheduled", "Accepted", "Converted to Patient", "Declined", "Closed"];

export default function Referrals() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [sort, setSort] = useState("newest");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/admin/referrals").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    let list = [...items];
    if (q) {
      const needle = q.toLowerCase();
      list = list.filter((r) =>
        [r.patient_name, r.patient_phone, r.referrer_name, r.referrer_clinic]
          .filter(Boolean).join(" ").toLowerCase().includes(needle)
      );
    }
    if (status) list = list.filter((r) => r.status === status);
    if (dateFrom) list = list.filter((r) => (r.submitted_at || "").slice(0, 10) >= dateFrom);
    if (sort === "oldest") list.sort((a, b) => (a.submitted_at || "").localeCompare(b.submitted_at || ""));
    else if (sort === "name") list.sort((a, b) => a.patient_name.localeCompare(b.patient_name));
    else list.sort((a, b) => (b.submitted_at || "").localeCompare(a.submitted_at || ""));
    return list;
  }, [items, q, status, dateFrom, sort]);

  if (!items) return <p className="text-slate-500 font-semibold">Loading referrals…</p>;

  return (
    <div data-testid="admin-referrals">
      <h1 className="font-display text-3xl md:text-4xl text-brand-navy">Referral Management</h1>
      <p className="mt-2 text-[15px] text-slate-600">All patient referrals submitted through the public website appear here automatically.</p>

      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            data-testid="referral-search"
            type="text"
            placeholder="Search by name, phone, or clinic…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full rounded-xl border border-slate-300 bg-white pl-11 pr-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-teal/60 focus:border-brand-teal"
          />
        </div>
        <select data-testid="referral-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-teal/60">
          <option value="">All statuses</option>
          {REFERRAL_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input data-testid="referral-date-filter" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-teal/60" aria-label="Filter from date" />
        <select data-testid="referral-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-teal/60">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="name">Patient name A–Z</option>
        </select>
      </div>

      <p data-testid="referrals-count" className="mt-4 text-sm font-semibold text-slate-500">{filtered.length} referral{filtered.length === 1 ? "" : "s"}</p>

      <div className="mt-4 hidden md:block rounded-2xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-left text-sm min-w-[900px]">
          <thead>
            <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3 font-bold">ID</th>
              <th className="px-4 py-3 font-bold">Patient</th>
              <th className="px-4 py-3 font-bold">Referral Date</th>
              <th className="px-4 py-3 font-bold">Phone</th>
              <th className="px-4 py-3 font-bold">Referring Person / Clinic</th>
              <th className="px-4 py-3 font-bold">Requested Service</th>
              <th className="px-4 py-3 font-bold">Preferred Start</th>
              <th className="px-4 py-3 font-bold">Status</th>
              <th className="px-4 py-3 font-bold">Staff</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr
                key={r.id}
                data-testid={`referral-row-${r.id}`}
                onClick={() => navigate(`/portal/referrals/${r.id}`)}
                className="border-b border-slate-100 hover:bg-brand-mist/40 cursor-pointer transition-colors duration-200"
              >
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{r.id.slice(0, 8)}</td>
                <td className="px-4 py-3 font-bold text-brand-navy">{r.patient_name}</td>
                <td className="px-4 py-3">{new Date(r.submitted_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">{r.patient_phone}</td>
                <td className="px-4 py-3">{r.referrer_name || r.referrer_clinic || "—"}</td>
                <td className="px-4 py-3">{r.requested_support?.[0] || "—"}{r.requested_support?.length > 1 ? ` +${r.requested_support.length - 1}` : ""}</td>
                <td className="px-4 py-3">{r.preferred_start_date || "—"}</td>
                <td className="px-4 py-3"><Badge kind="referral" value={r.status} /></td>
                <td className="px-4 py-3">{r.assigned_staff || "—"}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-500">No referrals match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 md:hidden space-y-3">
        {filtered.map((r) => (
          <button
            key={r.id}
            data-testid={`referral-card-${r.id}`}
            onClick={() => navigate(`/portal/referrals/${r.id}`)}
            className="w-full text-left rounded-2xl border border-slate-200 bg-white p-5"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-bold text-brand-navy">{r.patient_name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{new Date(r.submitted_at).toLocaleDateString()} · {r.patient_phone}</p>
              </div>
              <Badge kind="referral" value={r.status} />
            </div>
            <p className="mt-2 text-sm text-slate-600">{r.requested_support?.[0] || "No service selected"}</p>
          </button>
        ))}
        {filtered.length === 0 && <p className="text-center text-slate-500 py-10">No referrals match your filters.</p>}
      </div>
    </div>
  );
}
